import { useQuery } from 'urql';
import React from 'react';
import { getMetaAdAccounts } from '../graphql';
import { useProgressiveMetaAdAccounts } from './use-progressive-meta-ad-accounts';
import type { GetMetaAdAccountsQuery } from '../../../../gql/social/graphql';
import type { MetaAdAccount } from './types';

export interface UseMetaAdAccountsOptions {
  clientId: string;
  pause?: boolean;
  useProgressiveLoading?: boolean;
}

// Type aliases derived from the generated GraphQL types
type NonNullableClient = NonNullable<GetMetaAdAccountsQuery['client']>;
type Account = NonNullable<NonNullableClient['metaAdAccounts']>[number];
type InstagramAccount = NonNullable<NonNullableClient['instagramAccounts']>[number];
type Campaign = NonNullable<NonNullable<Account['metaAdCampaigns']>[number]>;
type AdSet = NonNullable<NonNullable<Campaign['metaAdSets']>[number]>;
type Ad = NonNullable<NonNullable<AdSet['metaAds']>[number]>;

// Extended types for progressive loading with additional properties
type ProgressiveCampaign = Campaign & {
  accountId: string;
  accountName?: string;
};

type ProgressiveAdSet = AdSet & {
  campaignId: string;
  campaignName?: string;
};

type ProgressiveAd = Ad & {
  adSetId: string;
  adSetName?: string;
};

// ProgressiveData interface using generated GraphQL types with flexibility for actual data structure
export interface ProgressiveData {
  accounts?: Array<MetaAdAccount>;
  instagramAccounts?: Array<
    | InstagramAccount
    | {
        accountId?: string;
        externalId?: string;
        pageId?: string;
        settings?: { adsPermissionsEnabled?: boolean; socialListeningEnabled?: boolean };
      }
  >;
  campaigns?: Array<
    | ProgressiveCampaign
    | {
        id: string;
        name?: string;
        objective?: string;
        status?: string;
        startTime?: string;
        stopTime?: string;
        accountId: string;
        accountName?: string;
      }
  >;
  adSets?: Array<
    | ProgressiveAdSet
    | {
        id: string;
        startTime?: string;
        stopTime?: string;
        name?: string;
        status?: string;
        campaignId: string;
        campaignName?: string;
      }
  >;
  ads?: Array<
    | ProgressiveAd
    | {
        id: string;
        status?: string;
        createdAt?: string;
        adAccountId?: string;
        name?: string;
        adSetId: string;
        adSetName?: string;
        metaAdMetaAdCreatives?: Array<{ metaAdCreative?: { sourceInstagramMediaId?: string } }>;
      }
  >;
}

/**
 * Transforms progressive loading data into the exact shape of the original getMetaAdAccounts query response
 * This ensures full backward compatibility with existing components
 */
export const transformProgressiveDataToOriginalShape = (progressiveData: ProgressiveData, clientId: string) => {
  const { accounts = [], instagramAccounts = [] } = progressiveData || {};
  const { campaigns = [], adSets = [], ads = [] } = progressiveData || {};

  // Transform accounts to include the full nested structure
  const transformedAccounts = accounts.map((account) => {
    // Find campaigns for this account
    const accountCampaigns = campaigns.filter((campaign) => campaign.accountId === account.id);

    // Transform campaigns to include ad sets
    const transformedCampaigns = accountCampaigns.map((campaign) => {
      // Find ad sets for this campaign
      const campaignAdSets = adSets.filter((adSet) => adSet.campaignId === campaign.id);

      // Transform ad sets to include ads
      const transformedAdSets = campaignAdSets.map((adSet) => {
        // Find ads for this ad set
        const adSetAds = ads.filter((ad) => ad.adSetId === adSet.id);

        // Return ad set with nested ads
        return {
          id: adSet.id,
          startTime: adSet.startTime,
          stopTime: adSet.stopTime,
          name: adSet.name,
          status: adSet.status,
          metaAds: adSetAds.map((ad) => ({
            id: ad.id,
            status: ad.status,
            createdAt: ad.createdAt,
            adAccountId: ad.adAccountId,
            name: ad.name,
            metaAdMetaAdCreatives: ad.metaAdMetaAdCreatives || [],
          })),
        };
      });

      // Return campaign with nested ad sets
      return {
        id: campaign.id,
        name: campaign.name,
        objective: campaign.objective,
        status: campaign.status,
        startTime: campaign.startTime,
        stopTime: campaign.stopTime,
        metaAdSets: transformedAdSets,
      };
    });

    // Return account with nested campaigns
    return {
      id: account.id,
      name: account.name,
      createdAt: account.createdAt,
      updatedAt: account.updatedAt,
      status: account.status,
      currentUserAccessLevel: account.currentUserAccessLevel,
      metaAdCampaigns: transformedCampaigns,
    };
  });

  // Return the exact shape of the original query response
  return {
    client: {
      id: clientId,
      instagramAccounts: instagramAccounts || [],
      metaAdAccounts: transformedAccounts,
    },
  };
};

export const useMetaAdAccounts = ({
  clientId,
  pause = false,
  useProgressiveLoading = true, // Default to progressive loading for better performance
}: UseMetaAdAccountsOptions) => {
  // Always call hooks at the top level to avoid conditional hook calls
  const progressiveHook = useProgressiveMetaAdAccounts({ clientId, pause });
  const fallbackQuery = useQuery({
    query: getMetaAdAccounts,
    variables: { clientId },
    pause: useProgressiveLoading ? true : pause, // Pause fallback query when using progressive loading
  });

  // Destructure stable values to avoid infinite re-renders
  const accounts = React.useMemo(() => progressiveHook.accounts || [], [progressiveHook.accounts]);
  const campaigns = React.useMemo(() => progressiveHook.campaigns || [], [progressiveHook.campaigns]);
  const selectedAccountIds = React.useMemo(
    () => progressiveHook.selectedAccountIds || [],
    [progressiveHook.selectedAccountIds],
  );
  const selectedCampaignIds = React.useMemo(
    () => progressiveHook.selectedCampaignIds || [],
    [progressiveHook.selectedCampaignIds],
  );
  const selectAccounts = progressiveHook.selectAccounts;
  const selectCampaigns = progressiveHook.selectCampaigns;

  // Auto-select all accounts to load campaigns and ad sets automatically
  // This ensures we get the same data as the original query
  React.useEffect(() => {
    if (useProgressiveLoading && accounts.length > 0 && selectedAccountIds.length === 0) {
      const allAccountIds = accounts.map((account) => account.id);
      selectAccounts(allAccountIds);
    }
  }, [useProgressiveLoading, accounts, selectedAccountIds, selectAccounts]);

  // Auto-select all campaigns to load ad sets automatically
  React.useEffect(() => {
    if (useProgressiveLoading && campaigns.length > 0 && selectedCampaignIds.length === 0) {
      const allCampaignIds = campaigns.map((campaign) => campaign.id);
      selectCampaigns(allCampaignIds);
    }
  }, [useProgressiveLoading, campaigns, selectedCampaignIds, selectCampaigns]);

  // Auto-select all ad sets to load ads automatically
  React.useEffect(() => {
    if (useProgressiveLoading && progressiveHook.adSets?.length > 0 && progressiveHook.selectedAdSetIds?.length === 0) {
      const allAdSetIds = progressiveHook.adSets.map((adSet) => adSet.id);
      progressiveHook.selectAdSets(allAdSetIds);
    }
  }, [useProgressiveLoading, progressiveHook]);

  // Use progressive loading by default for better performance
  if (useProgressiveLoading) {
    // Transform progressive data to match original query shape
    // Only transform if we have data, otherwise return empty structure
    const transformedData = progressiveHook.data
      ? transformProgressiveDataToOriginalShape(progressiveHook.data, clientId)
      : {
          client: {
            id: clientId,
            instagramAccounts: [],
            metaAdAccounts: [],
          },
        };

    // Return data in the exact same format as the original hook for backward compatibility
    return {
      data: transformedData,
      fetching: progressiveHook.isLoading,
      error: progressiveHook.hasError,
      // Additional progressive loading features for advanced usage
      progressive: progressiveHook,
    };
  }

  // Fallback to original query for backward compatibility
  const [{ data, fetching, error }] = fallbackQuery;

  return {
    data,
    fetching,
    error,
  };
};
